import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Slider from "react-slick";
import { getCountdown } from "../helper/Countdown";
import { ProductServices } from "../services/ProductsServices";
import ProductInfoServices from "../services/ProductInfoServices";
import { FlashSalesServices } from "../services/FlashSalesServices";
import QuantityControl from "../helper/QuantityControl";
import { getCustomerToken } from "../utils/CustomerAuth";
import { CartServices } from "../services/CartServices";
import { WishlistServices } from "../services/WishlistServices";
import api from "../api/api";

const API_BASE = "https://api.ifjona.com";

// Image URL helper
const toImageUrl = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/storage/")) return `${API_BASE}${s}`;
  if (s.startsWith("storage/")) return `${API_BASE}/${s}`;
  const clean = s.replace(/^\/+/, "").replace(/^storage\//, "");
  return `${API_BASE}/storage/${clean}`;
};

// Thumbnail slider arrows
const ThumbArrow = (props) => {
  const { onClick, direction, onNavigate } = props;
  return (
    <button
      type="button"
      className={`pd-thumbArrow pd-thumbArrow--${direction}`}
      aria-label={direction === "left" ? "Previous" : "Next"}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.(e);
        onNavigate?.();
      }}
    >
      <i className={`ph ph-caret-${direction === "left" ? "left" : "right"}`} />
    </button>
  );
};

const clampQty = (n, stockCount) => {
  const num = Number(n);
  let q = Number.isFinite(num) && num > 0 ? Math.floor(num) : 1;
  if (stockCount != null) {
    const s = Number(stockCount);
    if (Number.isFinite(s) && s > 0) q = Math.min(q, s);
  }
  return q;
};

/* Sticky (NO SIZE JUMP) */
const useStickyConstrained = ({ containerRef, stickyRef, measureRef, topOffset = 16 }) => {
  useEffect(() => {
    let rafId = null;

    const clear = (sticky) => {
      sticky.style.position = "";
      sticky.style.top = "";
      sticky.style.bottom = "";
      sticky.style.left = "";
      sticky.style.width = "";
    };

    const update = () => {
      const container = containerRef.current;
      const sticky = stickyRef.current;
      const measureEl = measureRef?.current;
      if (!container || !sticky || !measureEl) return;

      if (window.innerWidth < 992) {
        clear(sticky);
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const stickyHeight = sticky.offsetHeight;
      const containerTop = containerRect.top;
      const containerBottom = containerRect.bottom;
      const measureRect = measureEl.getBoundingClientRect();
      const left = measureRect.left;
      const width = measureRect.width;

      if (containerTop > topOffset) {
        sticky.style.position = "relative";
        sticky.style.top = "auto";
        sticky.style.bottom = "auto";
        sticky.style.left = "";
        sticky.style.width = "";
      } else if (containerBottom - topOffset >= stickyHeight) {
        sticky.style.position = "fixed";
        sticky.style.top = `${topOffset}px`;
        sticky.style.bottom = "auto";
        sticky.style.left = `${left}px`;
        sticky.style.width = `${width}px`;
      } else {
        sticky.style.position = "absolute";
        sticky.style.top = "auto";
        sticky.style.bottom = "0";
        sticky.style.left = "";
        sticky.style.width = "";
      }
    };

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(rafId);
    };
  }, [containerRef, stickyRef, measureRef, topOffset]);
};

// ✅ Star display — supports half stars
const StarDisplay = ({ value = 0, count = 5, size = 16 }) => {
  const clamped = Math.max(0, Math.min(count, Number(value) || 0));

  return (
    <div
      className="pd-stars"
      aria-label={`${clamped.toFixed(1)} out of ${count} stars`}
      style={{ display: "inline-flex", gap: 2 }}
    >
      {Array.from({ length: count }).map((_, i) => {
        const filled = clamped >= i + 1;
        const half = !filled && clamped > i && clamped < i + 1;

        return (
          <span
            key={i}
            style={{
              position: "relative",
              display: "inline-block",
              fontSize: size,
              lineHeight: 1,
              color: "#d1d5db",
            }}
          >
            <span style={{ color: "#d1d5db" }}>★</span>
            {(filled || half) && (
              <span
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  overflow: "hidden",
                  width: filled ? "100%" : "50%",
                  color: "#f59e0b",
                }}
              >
                ★
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
};

const ProductDetailsOne = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const productId = id;
  
  // ✅ Read URL search params (to get location filter like ShopSection does)
  const [searchParams] = useSearchParams();

  // Sticky refs
  const containerRef = useRef(null);
  const stickyRef = useRef(null);
  const leftColRef = useRef(null);

  useStickyConstrained({ containerRef, stickyRef, measureRef: leftColRef, topOffset: 80 });

  // slider ref + active index
  const thumbSliderRef = useRef(null);
  const [activeThumbIndex, setActiveThumbIndex] = useState(0);

  // ✅ COUNTDOWN - for flash sale
  const [flashSaleTick, setFlashSaleTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setFlashSaleTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  // LOADING / ERROR / DATA
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);
  const [productInfos, setProductInfos] = useState([]);
  
  // ✅ Flash sale state
  const [activeFlashSale, setActiveFlashSale] = useState(null);

  // ✅ Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // ✅ Fetch reviews from /api/products/{id}/reviews
  const fetchReviews = useCallback(async (pid) => {
    if (!pid) return;
    try {
      setReviewsLoading(true);
      const res = await api.get(`/products/${pid}/reviews`);
      const data = res?.data;

      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.reviews)
        ? data.reviews
        : [];

      setReviews(list);
    } catch (e) {
      console.error("Failed to load reviews", e);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  }, []);

  // ✅ Fetch active flash sale for this product
  const fetchFlashSale = useCallback(async (pid) => {
    if (!pid) return;
    try {
      const res = await FlashSalesServices.getFlashSales(true);
      const flashSales = Array.isArray(res?.data) ? res.data : [];
      
      const sale = flashSales.find((s) => String(s?.product_id) === String(pid));
      setActiveFlashSale(sale || null);
    } catch (e) {
      console.error("Failed to load flash sale", e);
      setActiveFlashSale(null);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!productId) throw new Error("Product ID missing in URL");

        const p = await ProductServices.getProductById(productId);
        const infos = await ProductInfoServices.getByProductId(productId);

        if (!mounted) return;
        setProduct(p || null);
        setProductInfos(Array.isArray(infos) ? infos : []);
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || "Failed to load product");
        setProduct(null);
        setProductInfos([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();
    return () => { mounted = false; };
  }, [productId]);

  // ✅ Fetch reviews and flash sale whenever productId changes
  useEffect(() => {
    fetchReviews(productId);
    fetchFlashSale(productId);
  }, [productId, fetchReviews, fetchFlashSale]);

  // ✅ Compute avg rating + count from approved customer reviews only
  const { avgRating, reviewCount } = useMemo(() => {
    const approved = reviews.filter((r) => {
      return (
        r?.is_approved === true ||
        r?.is_approved === 1 ||
        r?.status === "approved" ||
        r?.status === 1
      );
    });

    if (!approved.length) return { avgRating: 0, reviewCount: 0 };

    const sum = approved.reduce((acc, r) => {
      const n = Number(r?.rating ?? r?.stars ?? r?.score ?? 0);
      return acc + (Number.isFinite(n) ? n : 0);
    }, 0);

    return {
      avgRating: sum / approved.length,
      reviewCount: approved.length,
    };
  }, [reviews]);

  // VARIANT SELECTION
  const [selectedInfoId, setSelectedInfoId] = useState(null);
  useEffect(() => setSelectedInfoId(null), [productId]);

  const selectedInfo = useMemo(() => {
    if (!selectedInfoId) return null;
    return productInfos.find((x) => String(x?.id) === String(selectedInfoId)) || null;
  }, [productInfos, selectedInfoId]);

  // Build images
  const productImages = useMemo(() => {
    const arr = [];
    const main = toImageUrl(product?.image);
    if (main) arr.push({ url: main, infoId: null });

    for (const info of productInfos) {
      const u = toImageUrl(info?.image);
      if (u) arr.push({ url: u, infoId: info?.id ?? null });
    }

    const seen = new Set();
    return arr.filter((x) => x?.url && !seen.has(x.url) && seen.add(x.url));
  }, [product?.image, productInfos]);

  const [mainImage, setMainImage] = useState(null);

  useEffect(() => {
    const vImg = toImageUrl(selectedInfo?.image);
    if (vImg) {
      setMainImage(vImg);
      const idx = productImages.findIndex((x) => x.url === vImg);
      if (idx >= 0) setActiveThumbIndex(idx);
      return;
    }
    setMainImage(productImages[0]?.url || null);
    setActiveThumbIndex(0);
  }, [productImages, selectedInfo?.image]);

  const goToThumb = useCallback(
    (nextIndex) => {
      const len = productImages.length;
      if (!len) return;

      let idx = Number(nextIndex);
      if (!Number.isFinite(idx)) idx = 0;
      idx = Math.max(0, Math.min(len - 1, idx));

      const item = productImages[idx];
      if (!item) return;

      setActiveThumbIndex(idx);
      setMainImage(item.url);
      if (item.infoId) setSelectedInfoId(item.infoId);
      else setSelectedInfoId(null);

      thumbSliderRef.current?.slickGoTo?.(idx);
    },
    [productImages]
  );

  const settingsThumbs = useMemo(
    () => ({
      dots: false,
      infinite: productImages.length > 4,
      speed: 500,
      slidesToShow: Math.min(4, productImages.length || 1),
      slidesToScroll: 1,
      focusOnSelect: true,
      arrows: true,
      prevArrow: <ThumbArrow direction="left" onNavigate={() => goToThumb(activeThumbIndex - 1)} />,
      nextArrow: <ThumbArrow direction="right" onNavigate={() => goToThumb(activeThumbIndex + 1)} />,
      responsive: [
        { breakpoint: 992, settings: { slidesToShow: Math.min(4, productImages.length || 1) } },
        { breakpoint: 768, settings: { slidesToShow: Math.min(3, productImages.length || 1) } },
        { breakpoint: 576, settings: { slidesToShow: Math.min(3, productImages.length || 1) } },
        { breakpoint: 424, settings: { slidesToShow: Math.min(2, productImages.length || 1) } },
      ],
    }),
    [productImages.length, activeThumbIndex, goToThumb]
  );

  // Effective data
  const title = product?.title || "Product";
  const category = product?.category?.name  || "Category";

  const effectiveSpecification = useMemo(() => {
    const override = selectedInfo?.specification_override;
    if (override != null && String(override).trim() !== "") return override;
    return product?.base_specification || "";
  }, [product?.base_specification, selectedInfo?.specification_override]);

  const effectiveWarranty = useMemo(() => {
    const override = selectedInfo?.waranty_override;
    if (override != null && String(override).trim() !== "") return override;
    return product?.base_waranty || "";
  }, [product?.base_waranty, selectedInfo?.waranty_override]);

  // ✅ Get active location — first try URL param, then localStorage, then first location
  const locationFilter = useMemo(() => {
    return (searchParams.get("loc") || "").trim();
  }, [searchParams]);

  // ✅ FIXED: Determine active location for PRODUCT prices
  const { activeLocation: activeProductLocation, currencySymbol } = useMemo(() => {
    const productLocations = Array.isArray(product?.locations) ? product.locations : [];
    
    // Priority 1: Match location from URL filter
    if (locationFilter) {
      const matchedLoc = productLocations.find(
        (loc) => String(loc?.name || "").toLowerCase() === locationFilter.toLowerCase()
      );
      
      if (matchedLoc) {
        return {
          activeLocation: matchedLoc,
          currencySymbol: matchedLoc?.currency?.symbol || "$",
        };
      }
    }

    // Priority 2: Match from localStorage
    const selectedLocationName = localStorage.getItem("selectedLocation");
    if (selectedLocationName) {
      const locationsList = (() => {
        try { 
          return JSON.parse(localStorage.getItem("locationsList") || "[]"); 
        } catch { 
          return []; 
        }
      })();

      const activeLocationId = locationsList.find(
        (loc) => String(loc?.name || "").toLowerCase() === String(selectedLocationName || "").toLowerCase()
      )?.id ?? null;

      if (activeLocationId) {
        const matchedLoc = productLocations.find(
          (loc) => Number(loc?.id) === Number(activeLocationId)
        );
        
        if (matchedLoc) {
          return {
            activeLocation: matchedLoc,
            currencySymbol: matchedLoc?.currency?.symbol || "$",
          };
        }
      }
    }

    // Priority 3: Fallback to first available location
    const firstLoc = productLocations[0] ?? null;
    return {
      activeLocation: firstLoc,
      currencySymbol: firstLoc?.currency?.symbol || "$",
    };
  }, [product?.locations, locationFilter]);

  // ✅ NEW: Get variant's location price for the active location
  const variantLocationPrice = useMemo(() => {
    if (!selectedInfo?.location_prices || !activeProductLocation) return null;
    
    const locationPrices = Array.isArray(selectedInfo.location_prices) 
      ? selectedInfo.location_prices 
      : [];
    
    const found = locationPrices.find(
      (lp) => Number(lp?.location_id) === Number(activeProductLocation?.id)
    );
    
    return found || null;
  }, [selectedInfo?.location_prices, activeProductLocation]);

  // ✅ FIXED: Effective prices - variant location price takes priority
  const effectiveCurrentPrice = useMemo(() => {
    // If variant is selected and has a location price, use it
    if (variantLocationPrice) {
      const varFlash = variantLocationPrice?.flash_sale_price;
      if (varFlash != null && varFlash !== "" && Number(varFlash) > 0) {
        const varCurrent = Number(variantLocationPrice?.current_price_override || 0);
        if (Number(varFlash) < varCurrent) {
          return Number(varFlash);
        }
      }
      
      const varCurrent = variantLocationPrice?.current_price_override;
      if (varCurrent != null && varCurrent !== "") {
        return Number(varCurrent);
      }
    }
    
    // Fall back to product location price
    if (activeProductLocation) {
      const prodFlash = activeProductLocation?.flash_sales_price;
      if (prodFlash != null && prodFlash !== "" && Number(prodFlash) > 0) {
        const prodCurrent = Number(activeProductLocation?.price_current || 0);
        if (Number(prodFlash) < prodCurrent) {
          return Number(prodFlash);
        }
      }
      
      const locationPrice = activeProductLocation?.price_current;
      if (locationPrice != null) return Number(locationPrice);
    }
    
    return product?.base_current_price != null ? Number(product.base_current_price) : 0;
  }, [variantLocationPrice, activeProductLocation, product?.base_current_price]);

  const effectiveRegularPrice = useMemo(() => {
    // Variant location regular price
    if (variantLocationPrice?.regular_price_override != null) {
      return Number(variantLocationPrice.regular_price_override);
    }
    
    // Product location regular price
    if (activeProductLocation?.price_regular != null) {
      return Number(activeProductLocation.price_regular);
    }
    
    return product?.base_regular_price != null ? Number(product.base_regular_price) : null;
  }, [variantLocationPrice, activeProductLocation, product?.base_regular_price]);

  // ✅ Flash sale detection
  const isOnFlashSale = useMemo(() => {
    if (variantLocationPrice) {
      const varFlash = variantLocationPrice?.flash_sale_price;
      const varCurrent = variantLocationPrice?.current_price_override;
      return varFlash != null && varFlash !== "" && Number(varFlash) > 0 && Number(varFlash) < Number(varCurrent || 0);
    }
    
    if (activeProductLocation) {
      const prodFlash = activeProductLocation?.flash_sales_price;
      const prodCurrent = activeProductLocation?.price_current;
      return prodFlash != null && prodFlash !== "" && Number(prodFlash) > 0 && Number(prodFlash) < Number(prodCurrent || 0);
    }
    
    return false;
  }, [variantLocationPrice, activeProductLocation]);

  // ✅ Stock count from variant location price
  const stockCount = useMemo(() => {
    if (variantLocationPrice?.stock_count != null) {
      return Number(variantLocationPrice.stock_count);
    }
    return null;
  }, [variantLocationPrice]);

  // QUANTITY
  const [quantity, setQuantity] = useState(1);
  useEffect(() => setQuantity(1), [productId, selectedInfoId]);
  const onQtyChange = (nextQty) => setQuantity(clampQty(nextQty, stockCount));

  // ✅ PRICE DISPLAY - Using dynamic currency symbol
  const unitNewPriceText = useMemo(
    () => `${currencySymbol}${Number(effectiveCurrentPrice || 0).toFixed(2)}`,
    [currencySymbol, effectiveCurrentPrice]
  );

  const totalNewPriceText = useMemo(() => {
    const total = Number(effectiveCurrentPrice || 0) * Number(quantity || 1);
    return `${currencySymbol}${total.toFixed(2)}`;
  }, [currencySymbol, effectiveCurrentPrice, quantity]);

  const totalOldPriceText = useMemo(() => {
    if (effectiveRegularPrice == null) return null;
    return `${currencySymbol}${(Number(effectiveRegularPrice) * Number(quantity || 1)).toFixed(2)}`;
  }, [currencySymbol, effectiveRegularPrice, quantity]);

  // ✅ Flash sale countdown
  const flashSaleCountdown = useMemo(() => {
    if (!activeFlashSale?.end_at) return null;
    // eslint-disable-next-line no-unused-vars
    const _ = flashSaleTick;
    return getCountdown(activeFlashSale.end_at);
  }, [activeFlashSale?.end_at, flashSaleTick]);

  // ADD TO CART
  const [adding, setAdding] = useState(false);

  const handleAddToCart = async () => {
    const token = getCustomerToken();
    if (!token) {
      navigate("/login", { state: { from: `/product-details/${productId}` } });
      return;
    }
    try {
      setAdding(true);
      
      // ✅ FIXED: Build payload with correct field names
      const payload = {
        product_id: Number(productId),
        product_info_id: selectedInfo?.id ?? null,
        quantity: Number(quantity || 1),
      };

      // If variant is selected, pass product_info_location_price_id
      if (selectedInfo?.id && variantLocationPrice?.product_info_location_price_id) {
        payload.product_info_location_price_id = variantLocationPrice.product_info_location_price_id;
      } 
      // Otherwise pass location_product_price_id for base product
      else if (activeProductLocation?.location_product_price_id) {
        payload.location_product_price_id = activeProductLocation.location_product_price_id;
      }
      
      await CartServices.add(payload);
      window.dispatchEvent(new Event("cart_updated"));
    } catch (err) {
      console.error("Add to cart failed:", err);
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        navigate("/login", { state: { from: `/product-details/${productId}` } });
      }
    } finally {
      setAdding(false);
    }
  };

  // WISHLIST
  const [wishActive, setWishActive] = useState(false);
  const [togglingWish, setTogglingWish] = useState(false);

  const loadWishState = async () => {
    const token = getCustomerToken();
    if (!token) { setWishActive(false); return; }
    try {
      const items = await WishlistServices.getWishlist();
      const exists = (items || []).some((w) => {
        const sameProduct = String(w?.product_id ?? w?.product?.id ?? "") === String(productId);
        const wInfoId = w?.product_info_id ?? w?.productInfo?.id ?? null;
        const sameVariant =
          (selectedInfo?.id == null && (wInfoId == null || wInfoId === "")) ||
          String(wInfoId ?? "") === String(selectedInfo?.id ?? "");
        return sameProduct && sameVariant;
      });
      setWishActive(exists);
    } catch (e) {
      console.error("Failed to load wishlist state", e);
      setWishActive(false);
    }
  };

  useEffect(() => {
    loadWishState();
    const refresh = () => loadWishState();
    window.addEventListener("wishlist_updated", refresh);
    return () => window.removeEventListener("wishlist_updated", refresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, selectedInfoId]);

  const toggleWishlist = async () => {
    const token = getCustomerToken();
    if (!token) {
      navigate("/login", { state: { from: `/product-details/${productId}` } });
      return;
    }
    try {
      setTogglingWish(true);
      
      // ✅ Build payload with correct price IDs
      const payload = {
        product_id: Number(productId),
        product_info_id: selectedInfo?.id ?? null,
      };

      // ✅ If variant is selected, pass product_info_location_price_id
      // The field name should match what the API returns (check debug log)
      if (selectedInfo?.id && variantLocationPrice) {
        // ✅ TRY THESE FIELD NAMES (one of them should work):
        const pilpId = variantLocationPrice?.id 
                    || variantLocationPrice?.product_info_location_price_id
                    || variantLocationPrice?.pilp_id;
        
        if (pilpId) {
          payload.product_info_location_price_id = pilpId;
        }
      } 
      // ✅ Otherwise pass location_product_price_id for base product
      else if (activeProductLocation?.location_product_price_id) {
        payload.location_product_price_id = activeProductLocation.location_product_price_id;
      }
      
      const res = await WishlistServices.toggle(payload);
      
      if (typeof res?.is_wishlisted === "boolean") {
        setWishActive(res.is_wishlisted);
      } else {
        await loadWishState();
      }
      window.dispatchEvent(new Event("wishlist_updated"));
    } catch (err) {
      console.error("Wishlist toggle failed:", err);
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        navigate("/login", { state: { from: `/product-details/${productId}` } });
      }
    } finally {
      setTogglingWish(false);
    }
  };

  // RENDER STATES
  if (loading) {
    return (
      <section className="pd-wrap">
        <div className="pd-container">
          <div className="pd-loading">Loading product...</div>
        </div>
      </section>
    );
  }

  if (error || !product) {
    return (
      <section className="pd-wrap">
        <div className="pd-container">
          <div className="pd-error">
            <div className="pd-error-text">{error || "Product not found"}</div>
            <Link to="/shop" className="pd-btn pd-btn-primary">Back to Shop</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div className="pd-container">
      <div className="pd-grid" ref={containerRef} style={{ position: "relative" }}>

        {/* LEFT COLUMN */}
        <div className="pd-main" ref={leftColRef}>
          <div ref={stickyRef} className="pd-imagePanel">
            <div className="pd-imgBox">
              <div className="pd-mainImg">
                <img
                  src={mainImage || "/assets/images/thumbs/product-details-thumb1.png"}
                  alt={title}
                />
              </div>

              {!!productImages.length && (
                <div className="pd-thumbWrap pd-thumbSlider">
                  <Slider ref={thumbSliderRef} {...settingsThumbs}>
                    {productImages.map((imgObj, index) => (
                      <div key={`${imgObj.url}-${index}`} className="pd-slidePad">
                        <div
                          className={`pd-thumbCard ${mainImage === imgObj.url ? "active" : ""}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => goToThumb(index)}
                          onKeyDown={(e) => { if (e.key === "Enter") goToThumb(index); }}
                        >
                          <img src={imgObj.url} alt={`Thumbnail ${index}`} />
                        </div>
                      </div>
                    ))}
                  </Slider>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* MIDDLE COLUMN */}
        <div className="pd-content">
          <h5 className="pd-title">{title}</h5>

          {/* ✅ Dynamic rating — computed from actual approved customer reviews */}
          <div className="pd-metaRow">
            {reviewsLoading ? (
              <span style={{ fontSize: 13, color: "#9ca3af" }}>Loading rating...</span>
            ) : (
              <>
                <StarDisplay value={avgRating} count={5} size={16} />
                <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                  {avgRating > 0 ? avgRating.toFixed(1) : "0.0"} Star Rating
                </span>
                <span style={{ color: "#d1d5db" }}>|</span>
                <span style={{ fontSize: 13, color: "#6b7280" }}>
                  {reviewCount} {reviewCount === 1 ? "Review" : "Reviews"}
                </span>
                <span style={{ color: "#d1d5db" }}>|</span>
                <span style={{ fontSize: 13, color: "#6b7280" }}>
                  <strong>Category:</strong> {category}
                </span>
              </>
            )}
          </div>

          <div className="pd-sep" />

          {/* ✅ Active location indicator */}
          {activeProductLocation && (
            <div style={{ marginBottom: 12, fontSize: 13, color: "#6b7280" }}>
              <strong>Selected Location:</strong> {activeProductLocation.name}
            </div>
          )}

          {/* Variant selector */}
          {productInfos.length > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Select Variant:</div>
              <div className="pd-variantRow">
                <button
                  type="button"
                  className={`pd-pill ${!selectedInfoId ? "active" : ""}`}
                  onClick={() => setSelectedInfoId(null)}
                >
                  Default
                </button>
                {productInfos.map((v) => {
                  const active = String(v?.id) === String(selectedInfoId);
                  return (
                    <button
                      key={v.id}
                      type="button"
                      className={`pd-pill ${active ? "active" : ""}`}
                      onClick={() => setSelectedInfoId(v.id)}
                      title={v?.varient_value}
                    >
                      {v?.varient_value}
                    </button>
                  );
                })}
              </div>
              <div className="pd-sep" />
            </>
          )}

          {/* Specification */}
          {effectiveSpecification ? (
            <div className="pd-rich" dangerouslySetInnerHTML={{ __html: effectiveSpecification }} />
          ) : (
            <div className="pd-rich">No specification provided.</div>
          )}

          {effectiveWarranty ? (
            <div style={{ marginTop: 10, fontSize: 13 }}>
              <strong>Warranty:</strong> {effectiveWarranty}
            </div>
          ) : null}

          {stockCount != null ? (
            <div style={{ marginTop: 6, fontSize: 13 }}>
              <strong>Stock:</strong> {stockCount}
            </div>
          ) : null}

          {/* ✅ Price row — flash sale badge + prices with dynamic currency */}
          <div className="pd-priceRow" style={{ alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
            {isOnFlashSale && (
              <span style={{
                background: "rgba(239,68,68,0.1)",
                color: "#dc2626",
                fontSize: 12,
                fontWeight: 700,
                padding: "3px 10px",
                borderRadius: 999,
                whiteSpace: "nowrap",
              }}>
                🔥 Flash Sale
              </span>
            )}
            <div className="pd-priceNew" style={{ color: isOnFlashSale ? "#dc2626" : undefined }}>
              {totalNewPriceText}
            </div>
            {totalOldPriceText && <div className="pd-priceOld">{totalOldPriceText}</div>}
            <span style={{ marginLeft: 6, fontSize: 12, color: "#6B7280" }}>
              (Unit: {unitNewPriceText})
            </span>
          </div>

          <div className="pd-sep" />

          {/* ✅ Flash Sale Countdown */}
          {isOnFlashSale && flashSaleCountdown ? (
            <div className="pd-offer">
              <span style={{ fontWeight: 700, color: "#dc2626" }}>🔥 Flash Sale Ends In:</span>
              <div className="pd-count">
                <span>{flashSaleCountdown.days}</span>
                <span>{flashSaleCountdown.hours}</span>
                <span>{flashSaleCountdown.minutes}</span>
                <span>{flashSaleCountdown.seconds}</span>
              </div>
              <span>Grab it before the deal expires!</span>
            </div>
          ) : null}

          <div className="pd-sep" />

          {/* Quantity + actions */}
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Quantity:</div>

          <div className="pd-actions">
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <QuantityControl initialQuantity={quantity} onChange={onQtyChange} />
              <button
                type="button"
                className="pd-btn pd-btn-primary"
                disabled={stockCount === 0 || adding}
                onClick={handleAddToCart}
              >
                {adding ? "Adding..." : "🛒 Add To Cart"}
              </button>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                className="pd-iconBtn"
                onClick={toggleWishlist}
                disabled={togglingWish}
                title={wishActive ? "Remove from wishlist" : "Add to wishlist"}
                aria-label={wishActive ? "Remove from wishlist" : "Add to wishlist"}
                style={{ color: wishActive ? "#E11D48" : "#299E60", opacity: togglingWish ? 0.8 : 1 }}
              >
                ♥
              </button>
              <button type="button" className="pd-iconBtn" title="Shuffle" aria-label="Shuffle">⇄</button>
              <button type="button" className="pd-iconBtn" title="Share" aria-label="Share">⤴</button>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="pd-right">{/* keep your sidebar */}</div>
      </div>

      <div style={{ paddingTop: 50 }}>{/* keep your tabs */}</div>
    </div>
  );
};

export default ProductDetailsOne;